<template>
  <transition :name="$style.fade">
    <div
      v-if="isVisible"
      :class="$style.wrapper"
    >
      <div
        :class="[
          $style.announcement,
          'guide-message',
          [$style[type]]
        ]"
      >
        <div
          :class="[$style.caret, {
            [$style.caretLeft]: caretPosition === 'left',
            [$style.caretRight]: caretPosition === 'right',
            [$style.caretBottom]: caretPosition === 'bottom',
            [$style.caretBottomRight]: caretPosition === 'bottom-right',
            [customCaretClassName]: customCaretClassName,
          }]"
        />
        <header :class="$style.header">
          <CloseButton
            :class="$style.closeButton"
            :handleClose="handleDismiss"
          />
        </header>

        <p :class="$style.text">
          {{ body }}
        </p>
        <slot />

        <footer
          :class="$style.buttons"
        >
          <FlatActionButton
            v-if="showNext(name)"
            :action="handleActionButtonClick"
            :buttonClass="[$style.button]"
            :text="buttonText.mainAction"
          />
          <FlatActionButton
            v-if="showReset(name) "
            :action="handleRestartButtonClick"
            :buttonClass="[$style.link]"
            data-test-id="Announcement__dismiss"
            :text="buttonText.secondaryAction"
          />
        </footer>
      </div>
    </div>
  </transition>
</template>

<script>
import { eventProperties } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters, mapState } from 'vuex';
import CloseButton from 'components/Buttons/CloseButton.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';

const {
  description, iconName, location, text,
} = eventProperties;

export default {
  name: 'GuideMessage',

  components: {
    FlatActionButton,
    CloseButton,
  },
  EventHelper,
  props: {
    caretPosition: {
      type: String,
      default: 'right',
    },
    customCaretClassName: {
      type: String,
      default: '',
    },
    name: {
      type: String,
      default: '',
    },
    type: {
      type: String,
      default: 'informational',
    },
  },
  data: () => ({
    buttonText: {
      mainAction: 'Next Step',
      secondaryAction: 'Start Over',
    },
  }),

  computed: {
    ...mapState('GuidedSetupModule', [
      'guideName', 'currentStep',
    ]),
    ...mapGetters('GuidedSetupModule', [
      'guideText', 'showReset', 'showNext', 'currentStepName',
    ]),

    body() {
      return this.guideText(this.name);
    },

    isVisible() {
      return this.name === this.currentStepName;
    },
  },

  methods: {
    ...mapActions('GuidedSetupModule', {
      incrementStep: 'incrementStep',
      restartGuide: 'restartGuide',
      dismissGuide: 'dismissGuide',
    }),

    handleActionButtonClick() {
      this.$options.EventHelper.triggerButtonClicked({
        [location]: `${this.guideName} Guided Setup ${this.currentStepName}`,
        [description]: `${this.guideName} Guided Setup Next Button Clicked`,
        [text]: this.buttonText.mainAction,

      });
      this.incrementStep();
    },

    handleDismiss() {
      this.$options.EventHelper.triggerIconClicked({
        [location]: `${this.guideName} Guided Setup ${this.currentStepName}`,
        [description]: `${this.guideName} Guided Setup dismissed`,
        [iconName]: 'dismiss_guide',
      });
      this.dismissGuide();
    },

    handleRestartButtonClick() {
      this.$options.EventHelper.triggerButtonClicked({
        [location]: `${this.guideName} Guided Setup ${this.currentStepName}`,
        [description]: `${this.guideName} Guided Setup Secondary Action Clicked`,
        [text]: this.buttonText.secondaryAction,
      });
      this.restartGuide();
    },
  },
};
</script>

<style scoped module>
@value announcementZIndex from '~css/z-index.css';
@value (jcInformational200, jcPrimary) from '~css/brand-colors.css';
@value slowEaseFade from '~css/transitions.css';
@value arrowTop: 50% 0, 0 100%, 100% 100%;

.announcement {
  border-radius: 5px;
  color: var(--announcement-color);
  padding: 24px;
  position: absolute;
  width: 40ch;
  z-index: announcementZIndex;
}

.informational {
  --announcement-color: black;
  --button-color: var(--announcement-color);
  --announcement-background: jcInformational200;
}

.announcement,
.caret {
  background: jcInformational200;
}

.caret {
  clip-path: polygon(arrowTop);
  height: 12px;
  margin-top: 1px;
  position: absolute;
  top: -12px;
  width: 18px;
}

.caretBottom {
  top: calc(100% - 1px) !important;
  transform: rotate(180deg);
}

.caretRight {
  right: -15px;
  top: calc(50%);
  transform: rotate(90deg);
}

.caretBottomRight {
  right: 15px;
  top: calc(100% - 1px);
  transform: rotate(180deg);
}

.caretLeft {
  left: -15px;
  top: calc(50%);
  transform: rotate(270deg);
}

.header {
  display: grid;
  grid-gap: 24px;
  grid-template-columns: 1fr auto;
}

.closeButton {
  color: var(--button-color);
  transition: color 0.3s ease-in-out;
}

.closeButton:hover {
  color: var(--announcement-background);
}

.heading.heading {
  font-size: 18px;
  font-weight: 700;
  line-height: 1.25;
  margin: 0 0 1rem;
}

.text {
  margin: 1.5rem 0 1rem 0;
  text-align: left;
}

.buttons {
  display: flex;
  margin-top: 2rem;
}

.button.button {
  background: jcPrimary;
  color: handleRestartButtonClick !important;
}

.button.button:focus,
.button.button:hover {
  text-decoration: none;
}

.link {
  background: none;
  color: black;
  cursor: pointer;
  font-weight: normal;
  margin-left: auto;
  text-decoration: underline;
  width: auto;
}

.link >>> span {
  display: block;
}

.fade,
.fade-enter-active,
.fade-leave-active {
  transition: slowEaseFade;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.wrapper {
  height: 0;
  position: relative;
  width: 100%;
}
</style>
